import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useCurrencyMutations } from '~/hooks/use-currencies';
import { currencySchema, EMPTY_CURRENCY, type CurrencyFormValues } from '~/lib/schemas/currency';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: CurrencyFormValues | null;
}

/** Add / edit a currency (Νόμισμα). */
export function CurrencyEditDialog({ open, onOpenChange, initial }: Props) {
	const { t } = useTranslation();
	const { create, update } = useCurrencyMutations();
	const form = useForm<CurrencyFormValues>({
		resolver: zodResolver(currencySchema) as Resolver<CurrencyFormValues>,
		defaultValues: initial ?? EMPTY_CURRENCY,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const isEdit = Boolean(initial?.id);

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_CURRENCY);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	const visible = watch('iv');

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('prices.currencies.editTitle') : t('prices.currencies.addTitle')}</DialogTitle>
					<DialogDescription>{t('prices.currencies.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<FormField label={t('prices.currencies.columns.name')} error={formState.errors.nm?.message}>
						<Input {...register('nm')} maxLength={100} aria-invalid={Boolean(formState.errors.nm)} autoFocus />
					</FormField>
					<div className="grid grid-cols-2 gap-4">
						<FormField label={t('prices.currencies.columns.iso')} error={formState.errors.iso?.message}>
							<Input {...register('iso')} maxLength={3} aria-invalid={Boolean(formState.errors.iso)} />
						</FormField>
						<FormField label={t('prices.currencies.columns.format')} error={formState.errors.fs?.message}>
							<Input {...register('fs')} maxLength={10} aria-invalid={Boolean(formState.errors.fs)} />
						</FormField>
					</div>
					<div className="grid grid-cols-2 gap-4">
						<FormField label={t('prices.currencies.columns.sort')} error={formState.errors.si?.message}>
							<Input type="number" {...register('si', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.si)} />
						</FormField>
						<div className="flex items-end">
							<div className="flex w-full items-center justify-between rounded-md border border-border px-4 py-2">
								<Label htmlFor="currency-visible">{t('prices.currencies.columns.visible')}</Label>
								<Switch id="currency-visible" checked={visible} onCheckedChange={next => setValue('iv', next, { shouldDirty: true })} />
							</div>
						</div>
					</div>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
